import React, { useState } from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    BankOutlined,
    GlobalOutlined,
    ShopOutlined,
    InfoCircleOutlined,
    CalendarOutlined,
    SafetyOutlined,
    CheckCircleOutlined,
    PercentageOutlined,
    DollarOutlined,
    StopOutlined
} from "@ant-design/icons";
import { Col, Row, Tag, message, Button } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { capitalizeFirstLetter, formatDateTime } from "../../../../utils/functions";
import SOURCES from "../../../../utils/constants";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import CommonDialog from "../../../global/dialog";
import { useQueryClient } from "@tanstack/react-query";

const EnhancedAffiliateDetails = ({
    affiliateDetail,
    // showDetail,
    setShowDetail,
    getAffiliate
}) => {
    console.log(affiliateDetail)
    const [loading, setLoading] = useState(false);
    const [toggleModalOptions, setToggleModalOptions] = useState({
        showModal: false,
        title: "",
        postMessage: "",
        actionMethod: null,
        type: ""
    });

    const queryClient = useQueryClient()
    const approveRejectMutation = useGenericMutation({
        endpoint: "affiliate/approve-reject",
        onSuccess: () => {
            message.success("Affiliate status updated successfully");
            setShowDetail(false);
            getAffiliate();
        },
        onError: (error) => {
            message.error(error?.response?.data?.message || "Failed to update status");
        }
    });

    const handleApprovedReject = async (id, status) => {
        try {
            await approveRejectMutation.mutateAsync({ id, status });
        } catch (error) {
            console.error(error);
        }
    };

    if (!affiliateDetail) return null;

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Company: <BankOutlined />,
            "Company URL": <GlobalOutlined />,
            "Business Type": <ShopOutlined />,
            Type: <InfoCircleOutlined />,
            "Created At": <CalendarOutlined />,
            "Stripe Onboarded": <SafetyOutlined />,
            "User Status": <CheckCircleOutlined />,
            "Commission Type": <PercentageOutlined />,
            "Commission Value": <DollarOutlined />,
            "Additional Information": <InfoCircleOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    const getBadgeColor = (status, type) => {
        if (type === "stripe") {
            return status === "progress" ? "warning" : "success"; // Using warning for progress as per common UI, existing was danger -> danger? let's check.
            // Old code: affiliateDetail?.stripeConnectedStatus === "progress" ? "danger" : "success"
            // Wait, "progress" usually means incomplete/bad in this context? Okay, I will stick to "danger" if that's what it was, or maybe "warning".
            // Actually, in the screenshot "Stripe Onboarded" -> "Progress" is Red (Danger).
            // "User Status" -> "Pending" is Red (Danger).
        }
        if (type === "userStatus") {
            return status === SOURCES.STATUS_APPROVED ? "success" : "danger";
        }
        return "default";
    };

    const confirmAction = async () => {
        setLoading(true);
        if (toggleModalOptions.actionMethod) {
            await toggleModalOptions.actionMethod();
        }
        setLoading(false);
        setToggleModalOptions({ ...toggleModalOptions, showModal: false });
    };

    const SectionHeader = ({ icon, title }) => (
        <div className="flex items-center gap-2 mb-4 sm:mb-6">
            {React.cloneElement(icon, { style: { color: "var(--primary-color, #1890ff)", fontSize: "clamp(16px, 4vw, 18px)" } })}
            <span style={{ fontSize: "clamp(14px, 4vw, 16px)", fontWeight: 600 }}>{title}</span>
        </div>
    );

    const FieldItem = ({ label, value, isBadge, badgeColor, idx, total }) => (
        <Col span={24} className={cn("border-b border-gray-200 py-3 sm:py-4", idx === total - 1 && "!border-b-0")}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center w-full gap-1 sm:gap-4">
                <span className="text-[11px] sm:text-sm text-gray-500 sm:text-gray-600 flex items-center gap-2 font-medium">
                    <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "clamp(14px, 4vw, 16px)" }}>
                        {getIconForField(label)}
                    </span>
                    {label}
                </span>
                {isBadge ? (
                    <Tag color={badgeColor} className="m-0 px-3 py-0.5 sm:py-1 font-medium text-xs sm:text-sm">
                        {value}
                    </Tag>
                ) : (
                    <span className="text-sm sm:text-base font-semibold text-gray-900 break-words w-full sm:w-auto text-left sm:text-right">
                        {value || "—"}
                    </span>
                )}
            </div>
        </Col>
    );

    return (
        <>
            <div className="p-2 sm:p-6" style={{ backgroundColor: "#f5f7fa", minHeight: "100%" }}>
                <Row gutter={[16, 16]}>
                    {/* Basic Details */}
                    <Col xs={24} lg={12}>
                        <CommonCard cardHeadBodyClassName={{ head: "!px-6 !py-4", body: "!px-6 !py-4 !pb-4" }}>
                            <SectionHeader icon={<UserOutlined />} title="Basic Details" />
                            <Row>
                                {[
                                    { label: "Name", value: affiliateDetail?.name },
                                    { label: "Email", value: affiliateDetail?.email },
                                    { label: "Phone", value: affiliateDetail?.phone },
                                    { label: "Company", value: affiliateDetail?.companyName },
                                    { label: "Company URL", value: affiliateDetail?.companyUrl },
                                    { label: "Business Type", value: affiliateDetail?.bussinessType },
                                    { label: "Type", value: capitalizeFirstLetter(affiliateDetail?.userType) },
                                    { label: "Created At", value: formatDateTime(affiliateDetail?.createdAt) },
                                ].map((item, idx, arr) => (
                                    <FieldItem
                                        key={item.label}
                                        label={item.label}
                                        value={item.value}
                                        idx={idx}
                                        total={arr.length}
                                    />
                                ))}
                            </Row>
                        </CommonCard>
                    </Col>

                    {/* Status & Commission */}
                    <Col xs={24} lg={12}>
                        <div className="space-y-4 sm:space-y-6">

                            <CommonCard cardHeadBodyClassName={{ head: "!px-6 !py-4", body: "!px-6 !py-4 !pb-4" }}>
                                <SectionHeader icon={<CheckCircleOutlined />} title="Status & Commission" />
                                <Row>
                                    {[
                                        {
                                            label: "Stripe Onboarded",
                                            value: capitalizeFirstLetter(affiliateDetail?.stripeConnectedStatus),
                                            isBadge: true,
                                            badgeColor: affiliateDetail?.stripeConnectedStatus === "progress" ? "red" : "green"
                                        },
                                        {
                                            label: "User Status",
                                            value: capitalizeFirstLetter(affiliateDetail?.userStatus),
                                            isBadge: true,
                                            badgeColor: affiliateDetail?.userStatus === SOURCES.STATUS_APPROVED ? "green" : "red"
                                        },
                                        { label: "Commission Type", value: capitalizeFirstLetter(affiliateDetail?.commissionType) },
                                        {
                                            label: "Commission Value",
                                            value: affiliateDetail?.commissionValue + (affiliateDetail?.commissionType === "percentage" ? "%" : "")
                                        },
                                    ].map((item, idx, arr) => (
                                        <FieldItem
                                            key={item.label}
                                            label={item.label}
                                            value={item.value}
                                            isBadge={item.isBadge}
                                            badgeColor={item.badgeColor}
                                            idx={idx}
                                            total={arr.length}
                                        />
                                    ))}

                                    {affiliateDetail?.userStatus === "pending" && affiliateDetail?.userStatus !== "reject" && (
                                        <div className="w-full flex flex-col sm:flex-row gap-3 sm:gap-4 mt-3">
                                            <Button
                                                type="default"
                                                className="flex-1 !min-h-10 !text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white text-xs sm:text-sm font-medium"
                                                icon={<CheckCircleOutlined />}
                                                onClick={() => {
                                                    setToggleModalOptions({
                                                        showModal: true,
                                                        title: "Approve Affiliate/Partner",
                                                        postMessage: "Are you sure you want to approve affiliate/partner?",
                                                        actionMethod: () => handleApprovedReject(affiliateDetail?._id, SOURCES.STATUS_APPROVED)
                                                    });
                                                }}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                danger
                                                className="flex-1 !min-h-10 border !border-red-500 !text-red-500 hover:!bg-red-500 hover:!text-white text-xs sm:text-sm font-medium"
                                                icon={<StopOutlined />}
                                                onClick={() => {
                                                    setToggleModalOptions({
                                                        showModal: true,
                                                        title: "Reject Affiliate/Partner",
                                                        postMessage: "Are you sure you want to reject affiliate/partner?",
                                                        actionMethod: () => handleApprovedReject(affiliateDetail?._id, SOURCES.STATUS_REJECT)
                                                    });
                                                }}
                                            >
                                                Reject
                                            </Button>
                                        </div>
                                    )}
                                </Row>
                            </CommonCard>

                            {/* Additional Information */}
                            {affiliateDetail?.additionalInformation && (
                                <CommonCard>
                                    <SectionHeader icon={<InfoCircleOutlined />} title="Additional Information" />
                                    <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                        {affiliateDetail?.additionalInformation}
                                    </p>
                                </CommonCard>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>

            {toggleModalOptions.showModal && (
                <CommonDialog
                    title={toggleModalOptions.title}
                    description={toggleModalOptions.postMessage}
                    open={toggleModalOptions.showModal}
                    onCancel={() => setToggleModalOptions({ ...toggleModalOptions, showModal: false })}
                    onOk={confirmAction}
                    isConfirmButtonLoading={loading}
                />
            )}
        </>
    );
};

export default EnhancedAffiliateDetails; 